import { useContext } from "react";
import { useEffect, useRef } from "react";
import * as React from "react";

import type { Command, HotkeyRegistryEntry, KeyModifier } from "capitalrx-hotkeys";

import { HotkeyContext } from "../components/HotkeyProvider";
import type { UseHotkeysOptions } from "../types";

export { useHotkeyContext } from "../components/HotkeyProvider";
export { useHotkeyPermissions, useScopePermissions, setModuleMetadata, clearModuleMetadata } from "./useHotkeyPermissions";
export type { HotkeyPermissions, ScopePermissions, ScopeConfig, ModuleMetadata } from "./useHotkeyPermissions";

/**
 * Module registration counter for enforcing maxRegistrations limits.
 * Tracks the number of hotkeys registered by each module.
 */
const moduleRegistrationCounts: Map<string, number> = new Map();

/**
 * Per-scope registration counter for enforcing scope-level maxRegistrations limits.
 * Key format: "moduleId:scopeName"
 */
const scopeRegistrationCounts: Map<string, number> = new Map();

/**
 * Get the current registration count for a module.
 * @param moduleId The module ID to check.
 * @returns The current count of registered hotkeys for the module.
 */
export function getModuleRegistrationCount(moduleId: string): number {
  return moduleRegistrationCounts.get(moduleId) || 0;
}

/**
 * Get the current registration count for a specific scope within a module.
 * @param moduleId The module ID.
 * @param scopeName The scope name.
 * @returns The current count of registered hotkeys for the scope.
 */
export function getScopeRegistrationCount(moduleId: string, scopeName: string): number {
  const key = `${moduleId}:${scopeName}`;
  return scopeRegistrationCounts.get(key) || 0;
}

/**
 * Increment the registration count for a module.
 * @param moduleId The module ID to increment.
 * @returns The new count after incrementing.
 */
function incrementModuleRegistrationCount(moduleId: string): number {
  const current = moduleRegistrationCounts.get(moduleId) || 0;
  const newCount = current + 1;
  moduleRegistrationCounts.set(moduleId, newCount);
  return newCount;
}

/**
 * Increment the registration count for a specific scope.
 * @param moduleId The module ID.
 * @param scopeName The scope name.
 * @returns The new count after incrementing.
 */
function incrementScopeRegistrationCount(moduleId: string, scopeName: string): number {
  const key = `${moduleId}:${scopeName}`;
  const current = scopeRegistrationCounts.get(key) || 0;
  const newCount = current + 1;
  scopeRegistrationCounts.set(key, newCount);
  return newCount;
}

/**
 * Decrement the registration count for a module.
 * @param moduleId The module ID to decrement.
 * @returns The new count after decrementing.
 */
function decrementModuleRegistrationCount(moduleId: string): number {
  const current = moduleRegistrationCounts.get(moduleId) || 0;
  const newCount = Math.max(0, current - 1);
  moduleRegistrationCounts.set(moduleId, newCount);
  return newCount;
}

/**
 * Decrement the registration count for a specific scope.
 * @param moduleId The module ID.
 * @param scopeName The scope name.
 * @returns The new count after decrementing.
 */
function decrementScopeRegistrationCount(moduleId: string, scopeName: string): number {
  const key = `${moduleId}:${scopeName}`;
  const current = scopeRegistrationCounts.get(key) || 0;
  const newCount = Math.max(0, current - 1);
  scopeRegistrationCounts.set(key, newCount);
  return newCount;
}

/**
 * Reset registration counts for all modules or a specific module.
 * Useful for testing or when modules are unloaded.
 * @param moduleId Optional module ID to reset. If not provided, resets all.
 */
export function resetModuleRegistrationCounts(moduleId?: string): void {
  if (moduleId) {
    moduleRegistrationCounts.delete(moduleId);
    // Also reset all scope counts for this module
    for (const key of scopeRegistrationCounts.keys()) {
      if (key.startsWith(`${moduleId}:`)) {
        scopeRegistrationCounts.delete(key);
      }
    }
  } else {
    moduleRegistrationCounts.clear();
    scopeRegistrationCounts.clear();
  }
}

export function useHotkeysManager() {
  const context = useContext(HotkeyContext);
  if (!context) {
    throw new Error("useHotkeyManager must be used within HotKeyRoot");
  }
  return context.manager;
}

/**
 * Callback parameters for useHotkeys hook.
 * Using an object allows consumers to destructure only what they need.
 */
export interface HotkeyCallbackParams {
  /** The hotkey combo that was triggered (e.g., "CTRL+S") */
  combo: string;
  /** The original keyboard event */
  event: KeyboardEvent;
}

export function useHotkeys(
  combo: string,
  callback: (params: HotkeyCallbackParams) => void,
  options: UseHotkeysOptions = {},
) {
  const {
    enabled = true,
    priority = 50,
    scope = "global",
    title = "",
    description = "",
    category,
    // element is accepted for API compatibility but not used directly
    element: _element,
    allowInInput = false,
    preventDefault = true,
    stopPropagation = false,
  } = options;

  const manager = useHotkeysManager();

  // LAYER 3: Per-hook permission validation with strict scope enforcement
  const permissions = React.useMemo(() => {
    // Get permissions (only in browser context)
    if (typeof window === "undefined") {
      return {
        canRegister: true,
        canUseGlobalScope: true,
        allowedScopes: ["global"] as string[],
        moduleId: "unknown",
        strictMode: false,
        maxRegistrations: Infinity,
        scopeConfig: {} as Record<string, { enabled?: boolean; maxRegistrations?: number }>,
      };
    }

    // Check for runtime permission overrides (for QA testing)
    const runtimeOverride = (window as any).__HOTKEYS_PERMISSION_OVERRIDE__;

    const metadata = (window as any).__CAPITAL_RX_MODULE_METADATA__;
    if (!metadata) {
      // No metadata - use runtime overrides if available, otherwise no restrictions
      if (runtimeOverride) {
        return {
          canRegister: runtimeOverride.canRegister ?? true,
          canUseGlobalScope: runtimeOverride.canUseGlobalScope ?? true,
          allowedScopes: runtimeOverride.allowedScopes ?? ["global"],
          moduleId: "unknown",
          strictMode: runtimeOverride.strictMode ?? false,
          maxRegistrations: runtimeOverride.maxRegistrations ?? Infinity,
          scopeConfig: runtimeOverride.scopeConfig ?? {},
        };
      }
      // No metadata = no restrictions (backwards compatibility)
      return {
        canRegister: true,
        canUseGlobalScope: true,
        allowedScopes: ["global"] as string[],
        moduleId: "unknown",
        strictMode: false,
        maxRegistrations: Infinity,
        scopeConfig: {} as Record<string, { enabled?: boolean; maxRegistrations?: number }>,
      };
    }

    const perms = metadata.permissions || [];
    const features = metadata.features?.hotkeys || {};
    const hasRegisterPermission = perms.includes("hotkeys:register");
    const hasGlobalScopePermission = perms.includes("hotkeys:global-scope");
    const featureEnabled = features.enabled === true;

    // Base values from metadata
    let allowedScopes: string[] = features.scopes || [metadata.id];
    if (hasGlobalScopePermission || (featureEnabled && features.scopes?.includes("global"))) {
      if (!allowedScopes.includes("global")) {
        allowedScopes.push("global");
      }
    }

    // Get per-scope configuration
    let scopeConfig = features.scopeConfig || {};

    // Calculate base permissions
    let canRegister = hasRegisterPermission || featureEnabled;
    let canUseGlobalScope = hasGlobalScopePermission || (featureEnabled && features.scopes?.includes("global"));
    let strictMode = features.strictMode !== false; // Default to strict
    let maxRegistrations = features.maxRegistrations || 50;

    // Apply runtime overrides if present (for QA testing)
    if (runtimeOverride) {
      if (runtimeOverride.canRegister !== undefined) canRegister = runtimeOverride.canRegister;
      if (runtimeOverride.canUseGlobalScope !== undefined) canUseGlobalScope = runtimeOverride.canUseGlobalScope;
      if (runtimeOverride.allowedScopes !== undefined) allowedScopes = runtimeOverride.allowedScopes;
      if (runtimeOverride.strictMode !== undefined) strictMode = runtimeOverride.strictMode;
      if (runtimeOverride.maxRegistrations !== undefined) maxRegistrations = runtimeOverride.maxRegistrations;
      if (runtimeOverride.scopeConfig !== undefined) scopeConfig = { ...scopeConfig, ...runtimeOverride.scopeConfig };
    }

    return {
      canRegister,
      canUseGlobalScope,
      allowedScopes,
      moduleId: metadata.id,
      strictMode,
      maxRegistrations,
      scopeConfig,
    };
  }, []);

  const hotkeyIdRef = useRef<string | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // STRICT ENFORCEMENT: Block registration if module lacks permission
    if (!permissions.canRegister) {
      if (permissions.strictMode) {
        console.error(
          `[useHotkeys] BLOCKED: Module "${permissions.moduleId}" lacks permission to register hotkey "${combo}". ` +
            `Add "hotkeys:register" to module metadata.json permissions array.`,
        );
        return; // Block registration entirely
      } else {
        console.warn(
          `[useHotkeys] Module "${permissions.moduleId}" lacks permission to register hotkey "${combo}". ` +
            `Registration allowed in non-strict mode.`,
        );
      }
    }

    // STRICT ENFORCEMENT: Block registration if scope not allowed
    const isScopeAllowed = permissions.allowedScopes.includes(scope);
    if (!isScopeAllowed) {
      if (permissions.strictMode) {
        console.error(
          `[useHotkeys] BLOCKED: Module "${permissions.moduleId}" cannot register hotkey "${combo}" with scope "${scope}". ` +
            `Allowed scopes: [${permissions.allowedScopes.join(", ")}]. ` +
            `Add "${scope}" to features.hotkeys.scopes in metadata.json.`,
        );
        return; // Block registration entirely
      } else {
        console.warn(
          `[useHotkeys] Module "${permissions.moduleId}" using unauthorized scope "${scope}" for hotkey "${combo}". ` +
            `Registration allowed in non-strict mode.`,
        );
      }
    }

    // Legacy check for global scope (additional safeguard)
    if (scope === "global" && !permissions.canUseGlobalScope) {
      if (permissions.strictMode) {
        console.error(
          `[useHotkeys] BLOCKED: Module "${permissions.moduleId}" lacks permission for global scope hotkey "${combo}". ` +
            `Add "hotkeys:global-scope" to permissions array or add "global" to features.hotkeys.scopes.`,
        );
        return; // Block registration entirely
      }
    }

    // PER-SCOPE ENABLED CHECK: Check if scope is explicitly disabled
    const scopeConfig = permissions.scopeConfig[scope];
    if (scopeConfig?.enabled === false) {
      if (permissions.strictMode) {
        console.error(
          `[useHotkeys] BLOCKED: Scope "${scope}" is disabled for module "${permissions.moduleId}". ` +
            `Cannot register hotkey "${combo}". Set scopeConfig.${scope}.enabled=true in metadata.json.`,
        );
        return; // Block registration entirely
      } else {
        console.warn(
          `[useHotkeys] Scope "${scope}" is disabled for module "${permissions.moduleId}". ` +
            `Hotkey "${combo}" registered in non-strict mode.`,
        );
      }
    }

    // MAX REGISTRATION ENFORCEMENT: Check if module has exceeded its limit
    const currentCount = getModuleRegistrationCount(permissions.moduleId);
    if (currentCount >= permissions.maxRegistrations) {
      if (permissions.strictMode) {
        console.error(
          `[useHotkeys] BLOCKED: Module "${permissions.moduleId}" has reached maximum registrations (${permissions.maxRegistrations}). ` +
            `Cannot register hotkey "${combo}". Increase maxRegistrations in metadata.json or remove unused hotkeys.`,
        );
        return; // Block registration entirely
      } else {
        console.warn(
          `[useHotkeys] Module "${permissions.moduleId}" exceeded max registrations (${currentCount}/${permissions.maxRegistrations}). ` +
            `Hotkey "${combo}" registered in non-strict mode.`,
        );
      }
    }

    // PER-SCOPE MAX REGISTRATION ENFORCEMENT: Check scope-specific limit
    const scopeMaxRegistrations = scopeConfig?.maxRegistrations;
    if (scopeMaxRegistrations !== undefined) {
      const scopeCount = getScopeRegistrationCount(permissions.moduleId, scope);
      if (scopeCount >= scopeMaxRegistrations) {
        if (permissions.strictMode) {
          console.error(
            `[useHotkeys] BLOCKED: Scope "${scope}" has reached maximum registrations (${scopeMaxRegistrations}) for module "${permissions.moduleId}". ` +
              `Cannot register hotkey "${combo}". Increase scopeConfig.${scope}.maxRegistrations in metadata.json.`,
          );
          return; // Block registration entirely
        } else {
          console.warn(
            `[useHotkeys] Scope "${scope}" exceeded max registrations (${scopeCount}/${scopeMaxRegistrations}) for module "${permissions.moduleId}". ` +
              `Hotkey "${combo}" registered in non-strict mode.`,
          );
        }
      }
    }

    if (!enabled) {
      if (hotkeyIdRef.current) {
        manager.unregister?.(hotkeyIdRef.current);
        hotkeyIdRef.current = null;
        // Decrement counts when unregistering
        decrementModuleRegistrationCount(permissions.moduleId);
        decrementScopeRegistrationCount(permissions.moduleId, scope);
      }
      return;
    }

    const parts = combo.split("+").map((p) => p.trim().toLowerCase());
    const key = parts[parts.length - 1];
    const modifiers = parts.slice(0, -1) as KeyModifier[];

    // Use UIHotkeyConfig for type safety with extended UI properties
    const hotkeyConfig: import("../types").UIHotkeyConfig = {
      key,
      modifiers,
      scope,
      description: description || title,
      handler: (event: { originalEvent: KeyboardEvent }) => {
        callbackRef.current({ combo, event: event.originalEvent });
      },
      enabled: true,
      allowWhenInputFocused: allowInInput,
      preventDefault,
      stopPropagation,
      priority,
      // UI-specific fields
      title: title || description,
      category,
    };

    const id = manager.register?.(hotkeyConfig as import("capitalrx-hotkeys").HotkeyConfig);
    if (id) {
      hotkeyIdRef.current = id;
      // Increment counts when successfully registering
      incrementModuleRegistrationCount(permissions.moduleId);
      incrementScopeRegistrationCount(permissions.moduleId, scope);
    }

    return () => {
      if (hotkeyIdRef.current) {
        manager.unregister?.(hotkeyIdRef.current);
        hotkeyIdRef.current = null;
        // Decrement counts when unregistering
        decrementModuleRegistrationCount(permissions.moduleId);
        decrementScopeRegistrationCount(permissions.moduleId, scope);
      }
    };
  }, [manager, combo, enabled, priority, scope, title, description, category, allowInInput, preventDefault, stopPropagation, permissions]);
}
export function useHotkeysScope(scopeName: string, isActive: boolean) {
  const manager = useHotkeysManager();

  const activate = React.useCallback(() => {
    manager.activateScope?.(scopeName);
  }, [manager, scopeName]);

  const deactivate = React.useCallback(() => {
    manager.deactivateScope?.(scopeName);
  }, [manager, scopeName]);

  useEffect(() => {
    if (isActive) {
      activate();
    } else {
      deactivate();
    }
  }, [isActive, activate, deactivate]);

  return {
    activate,
    deactivate,
    isActive: manager.getActiveScope?.() === scopeName,
  };
}
export function useCommandPalette() {
  const manager = useHotkeysManager();
  const [isOpen, setIsOpen] = React.useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);
  const registerCommand = (command: Command) => {
    manager.getRegistry?.()?.registerCommand?.(command);
  };

  const unregisterCommand = (id: string) => {
    manager.getRegistry?.()?.unregisterCommand?.(id);
  };

  return {
    isOpen,
    open,
    close,
    toggle,
    registerCommand,
    unregisterCommand,
  };
}
export function useHotkeysEnabled() {
  const context = useContext(HotkeyContext);
  if (!context) {
    throw new Error("useHotkeysEnabled must be used within HotKeyRoot");
  }

  const { enabled, setEnabled } = context;

  const enable = () => setEnabled(true);
  const disable = () => setEnabled(false);
  const toggle: () => void = () => setEnabled(!enabled);

  return {
    enabled,
    enable,
    disable,
    toggle,
  };
}
export function useHotkeysHelp() {
  const [isOpen, setIsOpen] = React.useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
}

export function useModifierPopover() {
  const [isEnabled, setIsEnabled] = React.useState(true);

  const enable = () => setIsEnabled(true);
  const disable = () => setIsEnabled(false);
  const toggle = () => setIsEnabled((prev) => !prev);

  return {
    isEnabled,
    enable,
    disable,
    toggle,
  };
}

interface ContextualHint {
  combo: string;
  title: string;
  description: string;
  category?: string;
  priority: number;
}

interface UseContextualHintsOptions {
  /** Filter hints by specific scope. If not provided, uses current active scope. */
  scope?: string;
  /** Filter hints by category */
  category?: string;
  /** Maximum number of hints to return */
  limit?: number;
  /** Include global scope hints even when filtering by specific scope */
  includeGlobal?: boolean;
}

/**
 * Hook to get contextual hotkey hints based on current scope and focus.
 * Returns shortcuts filtered by the current active scope, enabling inline hints
 * near action buttons without duplicating registration logic.
 *
 * @example
 * const { hints, getHintForAction } = useContextualHints({ category: "Navigation" });
 * const saveHint = getHintForAction("Save"); // Returns { combo: "CTRL+S", ... } or undefined
 */
export function useContextualHints(options: UseContextualHintsOptions = {}) {
  const { scope, category, limit, includeGlobal = true } = options;
  const manager = useHotkeysManager();

  const hints = React.useMemo(() => {
    const allHotkeys: HotkeyRegistryEntry[] = manager.getHotkeys?.() || [];
    const activeScope = scope || manager.getActiveScope?.() || "global";

    let filtered = allHotkeys.filter((entry) => {
      const entryScope = entry.scope || entry.config?.scope || "global";

      // Match current scope or global (if includeGlobal is true)
      const scopeMatch = entryScope === activeScope || (includeGlobal && entryScope === "global");
      if (!scopeMatch) return false;

      // Filter by category if specified
      if (category) {
        const entryCategory = entry.config?.category;
        if (entryCategory !== category) return false;
      }

      // Only include enabled hotkeys
      if (entry.enabled === false) return false;

      return true;
    });

    // Sort by priority (highest first)
    filtered.sort((a, b) => {
      const priorityA = a.config?.priority ?? 50;
      const priorityB = b.config?.priority ?? 50;
      return priorityB - priorityA;
    });

    // Apply limit
    if (limit && limit > 0) {
      filtered = filtered.slice(0, limit);
    }

    // Transform to ContextualHint format
    return filtered.map((entry): ContextualHint => {
      const config = entry.config;
      const modifiers = config?.modifiers || [];
      const combo = modifiers.length > 0 ? `${modifiers.join("+")}+${config?.key}` : config?.key || "";

      return {
        combo: combo.toUpperCase(),
        title: config?.description || "Unnamed",
        description: config?.description || "",
        category: config?.category,
        priority: config?.priority ?? 50,
      };
    });
  }, [manager, scope, category, limit, includeGlobal]);

  /**
   * Get a specific hint by action title (case-insensitive partial match).
   * Useful for inline hints like: "Press {getHintForAction('Save')?.combo} to save"
   */
  const getHintForAction = React.useCallback(
    (actionTitle: string): ContextualHint | undefined => {
      const normalizedSearch = actionTitle.toLowerCase();
      return hints.find(
        (hint: ContextualHint) =>
          hint.title.toLowerCase().includes(normalizedSearch) || hint.description.toLowerCase().includes(normalizedSearch),
      );
    },
    [hints],
  );

  /**
   * Get hints for a specific category.
   */
  const getHintsForCategory = React.useCallback(
    (categoryName: string): ContextualHint[] => {
      return hints.filter((hint: ContextualHint) => hint.category?.toLowerCase() === categoryName.toLowerCase());
    },
    [hints],
  );

  return {
    /** All contextual hints matching the filter criteria */
    hints,
    /** Get a hint for a specific action by title */
    getHintForAction,
    /** Get all hints for a specific category */
    getHintsForCategory,
    /** Current active scope */
    activeScope: manager.getActiveScope?.() || "global",
  };
}

/**
 * Interface for shell hotkey registry entry (mirrors shell type).
 */
export interface ShellHotkeyEntry {
  id: string;
  combo: string;
  scope: string;
  moduleId: string;
  title?: string;
  description?: string;
  category?: string;
  priority: number;
  registeredAt: number;
}

/**
 * Interface for shell registry manager (mirrors shell type).
 */
export interface ShellHotkeyRegistryManager {
  register(entry: Omit<ShellHotkeyEntry, "id" | "registeredAt">): () => void;
  getAll(): ShellHotkeyEntry[];
  getByModule(moduleId: string): ShellHotkeyEntry[];
  getByScope(scope: string): ShellHotkeyEntry[];
  getByCombo(combo: string): ShellHotkeyEntry[];
  isRegistered(combo: string, scope: string): boolean;
  clearModule(moduleId: string): number;
  subscribe(listener: (entries: ShellHotkeyEntry[]) => void): () => void;
}

/**
 * Get the shell hotkey registry from context if available.
 * This is dynamically imported to avoid circular dependencies.
 * @returns The shell registry manager or null if not in shell context.
 */
export function getShellHotkeyRegistry(): ShellHotkeyRegistryManager | null {
  /* Try to get the registry from React context via window
     The shell sets this when HotkeyRegistryContext is provided */
  if (typeof window !== "undefined" && (window as any).__CAPITAL_RX_HOTKEY_REGISTRY__) {
    return (window as any).__CAPITAL_RX_HOTKEY_REGISTRY__ as ShellHotkeyRegistryManager;
  }
  return null;
}

/**
 * Set the shell hotkey registry reference (called by shell).
 * @param registry The registry manager instance.
 */
export function setShellHotkeyRegistry(registry: ShellHotkeyRegistryManager | null): void {
  if (typeof window !== "undefined") {
    (window as any).__CAPITAL_RX_HOTKEY_REGISTRY__ = registry;
  }
}

/**
 * Hook to access the shell-level hotkey registry.
 * Provides cross-module visibility into hotkey registrations.
 *
 * @returns Registry access methods or null if not in shell context.
 *
 * @example
 * ```tsx
 * const registry = useShellHotkeyRegistry();
 *
 * // Get all registered hotkeys
 * const allHotkeys = registry?.getAll() || [];
 *
 * // Check for conflicts
 * const isConflict = registry?.isRegistered("Ctrl+K", "global");
 *
 * // Get hotkeys by module
 * const moduleHotkeys = registry?.getByModule("documentation");
 * ```
 */
export function useShellHotkeyRegistry(): ShellHotkeyRegistryManager | null {
  const [registry, setRegistry] = React.useState<ShellHotkeyRegistryManager | null>(() => getShellHotkeyRegistry());

  React.useEffect(() => {
    // Re-check on mount in case registry was set after initial render
    const current = getShellHotkeyRegistry();
    if (current !== registry) {
      setRegistry(current);
    }
  }, []);

  return registry;
}

/**
 * Hook to register a hotkey in both the local manager and shell registry.
 * This allows cross-module visibility of hotkey registrations.
 *
 * @param combo The hotkey combination (e.g., "Ctrl+K").
 * @param callback The callback to invoke when the hotkey is pressed.
 * @param options Registration options.
 */
export function useShellTrackedHotkey(
  combo: string,
  callback: (params: HotkeyCallbackParams) => void,
  options: UseHotkeysOptions = {},
): void {
  const registry = useShellHotkeyRegistry();

  // Get current module metadata
  const metadata = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    return (window as any).__CAPITAL_RX_MODULE_METADATA__ || null;
  }, []);

  const {
    enabled = true,
    priority = 50,
    scope = "global",
    title = "",
    description = "",
    category,
  } = options;

  // Register with shell registry for cross-module visibility
  React.useEffect(() => {
    if (!registry || !enabled) return;

    const moduleId = metadata?.id || "unknown";

    const unregister = registry.register({
      combo,
      scope,
      moduleId,
      title,
      description,
      category,
      priority,
    });

    return () => {
      unregister();
    };
  }, [registry, combo, scope, title, description, category, priority, enabled, metadata?.id]);

  // Also register with local manager using existing hook
  useHotkeys(combo, callback, options);
}
